// KPI configuration that reads from kpi-config.json
// This provides a TypeScript interface for the JSON configuration

// Import the JSON configuration
import kpiConfigData from './kpi-config.json';

export interface KpiMeta {
  name: string;
  shortName: string;
  type: 'poäng' | 'andel' | 'kvot' | 'antal' | 'procent' | 'kr';
  unit: string;
  deltaUnit: string;
  higherIsBetter: boolean | null;
  description?: string;
  category: 'resultat_ak9' | 'resultat_ak6' | 'resurser_struktur';
  group: 'ak9' | 'ak6' | 'resources';
  sortOrder: number;
}

export interface KpiConfig {
  cache: {
    ttl_minutes: number;
  };
  display: {
    show_warnings: boolean;
    max_dropdown_items: number;
  };
  categories: {
    [key: string]: {
      name: string;
      icon: string;
    };
  };
}

// Export the full configuration
export const kpiConfig: KpiConfig = kpiConfigData.config;

// Map JSON config to our TypeScript interface
export const kpiMeta: Record<string, KpiMeta> = {};

// Initialize the kpiMeta object from config data
kpiConfigData.kpis.forEach((kpi: any) => {
  kpiMeta[kpi.id] = {
    name: kpi.name,
    shortName: kpi.short_name,
    type: kpi.type,
    unit: kpi.unit,
    deltaUnit: kpi.delta_unit,
    higherIsBetter: kpi.better_is_higher,
    description: kpi.description,
    category: kpi.category,
    group: kpi.group,
    sortOrder: kpi.sort_order
  };
});

// Helper functions for formatting
export const formatValue = (value: number, type: KpiMeta['type']): string => {
  if (isNaN(value) || !isFinite(value)) return 'N/A';
  
  switch (type) {
    case 'poäng':
      return value.toFixed(1);
    case 'andel':
    case 'procent':
      return value.toFixed(1);
    case 'kvot':
      return value.toFixed(1);
    case 'antal':
      return Math.round(value).toLocaleString('sv-SE');
    case 'kr':
      return Math.round(value).toLocaleString('sv-SE');
    default:
      return value.toFixed(1);
  }
};

export const formatDelta = (delta: number, deltaUnit: string): string => {
  if (isNaN(delta) || !isFinite(delta)) return 'N/A';
  
  const sign = delta >= 0 ? '+' : '';
  
  if (deltaUnit === 'p.p.') {
    return `${sign}${delta.toFixed(1)} p.p.`;
  } else if (deltaUnit === 'p') {
    return `${sign}${delta.toFixed(1)} p`;
  } else if (deltaUnit === 'st/HT') {
    return `${sign}${delta.toFixed(1)} st/HT`;
  } else if (deltaUnit === 'kr') {
    return `${sign}${Math.round(delta).toLocaleString('sv-SE')} kr`;
  } else {
    return `${sign}${delta.toFixed(1)} ${deltaUnit}`;
  }
};

export const getDeltaColor = (delta: number, higherIsBetter: boolean | null): string => {
  if (higherIsBetter === null) return 'text-slate-600'; // Neutral for metrics like "antal elever"
  
  if (delta > 0) {
    return higherIsBetter ? 'text-emerald-600' : 'text-rose-600';
  } else if (delta < 0) {
    return higherIsBetter ? 'text-rose-600' : 'text-emerald-600';
  } else {
    return 'text-slate-600';
  }
};

export const getDeltaBgColor = (delta: number, higherIsBetter: boolean | null): string => {
  if (higherIsBetter === null) return 'bg-slate-100 text-slate-800';
  
  if (delta > 0) {
    return higherIsBetter ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800';
  } else if (delta < 0) {
    return higherIsBetter ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800';
  } else {
    return 'bg-slate-100 text-slate-800';
  }
};